import { BadStateError, UNITS } from '@skand/utils';
import { atom } from 'jotai';
import { v4 as uuid } from 'uuid';
import { logger } from '../../logger';
import { JotaiStore } from '../../stores/jotaiStore';
import { DownloadContext } from '../DownloadContext';
import { Progress } from '../Progress/Progress';
import { InvalidContentLengthError, isAbortionError, isNotFoundError } from '../errors';
import { DownloaderStage, IDownloader } from './IDownloader';
import { queue } from './queue';

export class StreamDownloader implements IDownloader {
  private _downloadPromise: null | Promise<File> = null;
  private _progress: Progress;
  private _store: JotaiStore;
  private _uuid = uuid();
  readonly range: null | [number, number] = null;
  readonly url: string;

  private _dataAtom = atom<null | File>(null);
  private _errorAtom = atom<null | unknown>(null);
  private _stageAtom = atom<DownloaderStage>('initial');

  readonly dataAtom = atom(get => get(this._dataAtom));
  readonly errorAtom = atom(get => get(this._errorAtom));
  readonly stageAtom = atom(get => get(this._stageAtom));

  constructor(args: {
    url: string;
    contentLength?: number;
    downloadContext: DownloadContext;
    range?: [number, number];
  }) {
    this._progress = new Progress({
      bytesTotal: args.contentLength,
      downloadContext: args.downloadContext,
    });
    this._store = args.downloadContext.jotaiStore;
    this.range = args.range ?? null;
    this.url = args.url;
  }

  async start(signal?: AbortSignal) {
    const currentStage = this._store.get(this._stageAtom);

    if (currentStage === 'aborted' || currentStage === 'failure') {
      // retry
      this._downloadPromise = null;
      this._progress.reset();
      this._store.set(this._dataAtom, null);
      this._store.set(this._errorAtom, null);
      this._store.set(this._stageAtom, 'initial');
    }

    if (this._downloadPromise === null) {
      this._downloadPromise = this._download(signal);
      this._store.set(this._stageAtom, 'downloading');
    } else if (signal) {
      logger.warn('signal is ignored because the download has already been started');
    }

    try {
      const response = await this._downloadPromise;
      return response;
    } catch (error) {
      this._store.set(this._errorAtom, error);

      if (isAbortionError(error)) {
        logger.warn(error);
        this._store.set(this._stageAtom, 'aborted');
      } else {
        this._store.set(this._stageAtom, 'failure');
      }

      throw error;
    }
  }

  private async _download(signal?: AbortSignal) {
    const headers = this.range ? { Range: `bytes=${this.range[0]}-${this.range[1]}` } : undefined;

    const downloadFn = async () => {
      const opfsRoot = await navigator.storage.getDirectory();
      const opfsFileHandler = await opfsRoot.getFileHandle(this._uuid, { create: true });
      const opfsFileWritableStream = await opfsFileHandler.createWritable();

      const response = await fetch(this.url, {
        method: 'GET',
        signal,
        headers: new Headers(headers),
      });

      const reader = response.body?.getReader({ mode: 'byob' });
      if (!reader) throw new BadStateError('empty body or invalid readable stream');

      const writer = opfsFileWritableStream.getWriter();

      const contentLengthString = response.headers.get('content-length');
      const contentLength = Number(contentLengthString);
      if (!Number.isInteger(contentLength))
        throw new InvalidContentLengthError(contentLengthString);

      try {
        let wroteBytes = 0;
        while (true) {
          const buffer = new Uint8Array(UNITS.BYTE.MiB);
          const result = await reader.read(buffer);
          if (result.done) {
            await writer.close();
            this._progress.update(contentLength, contentLength);
            break;
          }

          await writer.ready;
          await writer.write(result.value);

          wroteBytes += result.value.length;
          this._progress.update(wroteBytes, contentLength);
        }
      } catch (error) {
        await reader.cancel(error);
        await writer.abort(error);
        throw error;
      }

      return opfsFileHandler;
    };

    const fileHandler = await queue.add(downloadFn, { signal });
    // queue.add returns void when the task timed out. We know this is not our case.
    // We can safely assume that the blob is defined.
    // @see https://github.com/sindresorhus/p-queue/blob/main/source/index.ts#L236
    if (!fileHandler) throw new BadStateError('invalid blob');
    const file = await fileHandler.getFile();

    this.progress.finish();
    this._store.set(this._stageAtom, 'success');
    this._store.set(this._dataAtom, file);

    return file;
  }

  async clean() {
    const opfsRoot = await navigator.storage.getDirectory();
    try {
      await opfsRoot.removeEntry(this._uuid);
    } catch (error) {
      if (isNotFoundError(error)) {
        // File does not exist. It may be cleaned by browser.
        // No need to take action. Just log it for debugging.
        console.warn(error);
        return;
      }

      throw error;
    }
  }

  get progress() {
    return this._progress;
  }
}
